import { scrollToAnchor, throttle } from '@cian/newbuilding-utils';
import { useIntersectionObserver, useUpdateEffect } from '@cian/react-utils';

import { type FC, type SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';

import { PikLogoContainer } from 'shared/components/PikLogo';
import { Callback } from 'shared/containers/Callback';
import { PikPhoneNumber } from 'shared/containers/PikPhoneNumber';
import { type INavLinks, type TSectionAnchor } from 'shared/settings/routes';
import { ECallbackPlace } from 'shared/types/callback';
import { setHash } from 'shared/utils/setHash';

import { HeaderV2 } from './HeaderV2';
import { trackHeaderNavSectionClick } from './internal/tracking/trackHeaderNavSectionClick';
import { trackShowHeaderSection } from './internal/tracking/trackShowHeaderSection';
import { getNavLinksFromSections } from './internal/utils/getNavLinksFromSections';
import { updateActiveSection } from './internal/utils/updateActiveSection';

const STICKY_TOP = 22;

export const HeaderV2Container: FC = () => {
  const [isSticky, setSticky] = useState(false);
  const [navLinks, setNavLinks] = useState<INavLinks[]>([]);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setNavLinks(getNavLinksFromSections());
  }, []);

  useUpdateEffect(() => {
    if (!navLinks.length) {
      return undefined;
    }

    const sectionsChangeListener = throttle(600, () => updateActiveSection(navLinks));

    window.addEventListener('scroll', sectionsChangeListener, { passive: true });

    return (): void => {
      window.removeEventListener('scroll', sectionsChangeListener);
    };
  }, [navLinks]);

  const updateStickyState = useCallback(() => {
    const header = headerRef.current;

    if (header) {
      setSticky(header.getBoundingClientRect().top <= STICKY_TOP);
    }
  }, []);

  useEffect(() => {
    const stickyStateChangeListener = throttle(200, updateStickyState);

    updateStickyState();
    window.addEventListener('scroll', stickyStateChangeListener, { passive: true });

    return (): void => {
      window.removeEventListener('scroll', stickyStateChangeListener);
    };
  }, [updateStickyState]);

  const handleLinkClick = useCallback((value: TSectionAnchor, event: SyntheticEvent): void => {
    trackHeaderNavSectionClick(value);

    scrollToAnchor({ anchor: `#${value}`, hasRelocation: false, offset: 50 })(event);

    setHash(value);
  }, []);

  useIntersectionObserver({
    elementRef: headerRef,
    onInViewport: ({ subscribeState }) => {
      subscribeState.setSubscribeUnavailable();
      trackShowHeaderSection();
    },
  });

  return (
    <HeaderV2
      callback={<Callback place={isSticky ? ECallbackPlace.BlockStickyTop : ECallbackPlace.HeadBlock} />}
      isSticky={isSticky}
      logo={<PikLogoContainer />}
      navLinks={navLinks}
      number={<PikPhoneNumber />}
      ref={headerRef}
      onNavSectionClick={handleLinkClick}
    />
  );
};
